import React, { useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";

export interface SearchFieldConfig<T> {
  label: string;
  key: keyof T;
  type?: "text" | "number" | "date";
  placeholder?: string;
}

interface GenericSearchProps {
  query: string;
  placeholder: string;
  fullList?: boolean;
  setQuery: (value: string) => void;
  onSearch: () => void;
  onReset?: () => void;
}

export function GenericSearch({
  query,
  placeholder,
  fullList = false,
  setQuery,
  onSearch,
  onReset,
}: GenericSearchProps) {
  const [searchMode, setSearchMode] = React.useState<"filter" | "all">(
    fullList ? "all" : "filter"
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchMode === "filter") {
      setQuery("");
      onSearch();
    }
  };

  const handleModeChange = (value: "filter" | "all") => {
    setSearchMode(value);
    onReset?.();

    if (value === "all") {
      setQuery("");
      onSearch();
    }
  };

  useEffect(() => {
    if (searchMode === "all") {
      onSearch();
    }
  }, []);

  return (
    <Box
      sx={{
        p: 2,
        border: "1px solid #ccc",
        borderRadius: 2,
        backgroundColor: "#ffffff",
        color: "#2c4397ff",
      }}
    >
      <form onSubmit={handleSubmit}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            flexWrap: { xs: "wrap", md: "nowrap" },
          }}
        >
          {/* Mode Select */}
          <FormControl
            sx={{
              width: { xs: "100%", md: "12%" },
              minWidth: 150,
            }}
            size="small"
          >
            <InputLabel>Mode</InputLabel>
            <Select
              value={searchMode}
              label="Mode"
              onChange={(e) =>
                handleModeChange(e.target.value as "filter" | "all")
              }
            >
              <MenuItem value="filter">Filter</MenuItem>
              <MenuItem value="all">Show All</MenuItem>
            </Select>
          </FormControl>

          {/* Filter Fields */}
          <TextField
            key="query"
            name="query"
            variant="outlined"
            size="small"
            label="Search Text"
            value={query}
            placeholder={placeholder}
            disabled={searchMode === "all"}
            onChange={(e) => setQuery(e.target.value)}
            sx={{
              width: { xs: "100%", md: "45%" },
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />

          {/* Buttons */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              gap: 2,
              width: { xs: "100%", md: "auto" },
            }}
          >
            <Button
              type="submit"
              variant="contained"
              color="success"
              disabled={searchMode === "all"}
              sx={{
                width: { xs: "45%", md: "auto" },
              }}
            >
              Search
            </Button>

            <Button
              type="button"
              variant="outlined"
              color="warning"
              onClick={onReset}
              sx={{
                width: { xs: "45%", md: "auto" },
              }}
            >
              Reset
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
}
